import { useId, useMemo, useState, type ComponentProps } from "react";
import type { SelectChangeEvent } from "@mui/material";
import {
  Box,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import type {
  AvailableOrganisationUnbranded,
  OrganisationRole,
  TranslationKeyAndOptions,
} from "@thalamos/vision-common";

import { FormError } from "../FormError/FormError.js";
import { FormLabel } from "../FormLabel/FormLabel.js";
import { useOrganisations } from "./OrganisationContext.js";

const debug = debugFn("vision-frontend:OrganisationDropdown");

export type DropdownOutput =
  | {
      value: string;
      valueLabel: string;
    }
  | "";

export type OrganisationDropdownProps = {
  fieldName: string;
  label?: TranslationKeyAndOptions;
  filterByRoles?: OrganisationRole[];
  /**
   * When true, organisations flagged as `excludeAsIncidentLocation` will be
   * included in the dropdown options. Defaults to false so that those
   * organisations are hidden from incident-location selectors.
   *
   * Set this to true in contexts such as the onboarding home-base selector.
   */
  includeExcludedAsIncidentLocation?: boolean;
} & Pick<ComponentProps<typeof Select>, "autoFocus">;

export function OrganisationDropdown({
  fieldName,
  label,
  filterByRoles,
  includeExcludedAsIncidentLocation = false,
  autoFocus,
}: OrganisationDropdownProps) {
  const { t } = useTranslation();
  const ariaId = useId();
  const organisationContext = useOrganisations();

  const [field, meta, helpers] = useField<DropdownOutput>(fieldName);

  // We use this to store the last value that the user selected.
  // This is so that we can restore it if they turn on the "unknown" flag.
  // We pre-populate this with the incoming "initialValue".
  const [selectedValue, setSelectedValue] = useState<string | null>(
    field.value === "" ? null : field.value.value,
  );

  // Clean up the field value to be a structured object
  const fieldValue = useMemo(() => {
    const value =
      field.value === ""
        ? // Turn null-ish values into a structured object
          ({ value: null, valueLabel: null } as const)
        : { value: field.value.value, valueLabel: field.value.valueLabel };

    debug("Cleaned up value", { value });
    return value;
  }, [field.value]);

  const filteredOptions = useMemo(() => {
    let options = filterByRoles
      ? organisationContext.organisations.filter((option) =>
          filterByRoles.includes(option.role),
        )
      : organisationContext.organisations;

    if (!includeExcludedAsIncidentLocation) {
      options = options.filter((option) => !option.excludeAsIncidentLocation);
    }

    return options;
  }, [
    filterByRoles,
    includeExcludedAsIncidentLocation,
    organisationContext.organisations,
  ]);

  // The error could either come from Formik ("Invalid input") or from the schema
  // ({value: "string too short"}). We need to figure out which at runtime.
  const fieldError = useMemo(
    () => {
      const value =
        typeof meta.error === "string"
          ? meta.error
          : typeof meta.error === "object"
            ? (meta.error as { value: string } | undefined)?.value
            : undefined;

      return value === undefined ? undefined : t(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(meta.error)],
  );
  if (fieldError) {
    debug("fieldError", fieldError);
  }

  const handleValueChange = (event: SelectChangeEvent) => {
    const runAsync = async () => {
      const selectedValue = event.target.value;

      const selectedValueLabel =
        filteredOptions.find((option) => option.id === selectedValue)?.name ??
        "";
      const newValue = {
        value: selectedValue,
        valueLabel: selectedValueLabel,
      };

      debug("handleValueChange, setting value", newValue);
      setSelectedValue(selectedValue);
      await helpers.setValue(newValue);
      await helpers.setTouched(true);
    };

    void runAsync();
  };

  return (
    <Box>
      <FormControl fullWidth data-testid={`formField-${fieldName}`}>
        {label && <FormLabel {...label} id={ariaId} />}
        <Select
          name={fieldName}
          value={selectedValue ?? ""}
          onChange={handleValueChange}
          onBlur={field.onBlur}
          data-testid="select-dropdown"
          labelId={ariaId}
          autoFocus={autoFocus}
        >
          {filteredOptions.map((option: AvailableOrganisationUnbranded) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
        {meta.touched && fieldError && <FormError error={fieldError} />}
      </FormControl>
      {debug.enabled && (
        <>
          <Divider />
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {"field.value : "}
            {JSON.stringify(field.value)}
          </Typography>
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {"Treated like: "}
            {JSON.stringify(fieldValue)}
          </Typography>
          <Divider />
        </>
      )}
    </Box>
  );
}
